import { Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { roleRank, useSession } from "@vixio/data";
import { PageSkeleton } from "@vixio/ui";
import { useAuth } from "./auth/AuthProvider";
import { FirstAccessPage, LoginPage, RecoverPasswordPage, ResetPasswordPage } from "./auth/LoginPage";
import { SessionGate } from "./shell/SessionGate";
import { ScreenBoundary } from "./shell/ScreenBoundary";
import { Layout } from "./shell/Layout";
import { registeredModules, routesFor } from "./shell/registry";
import { useActiveModules } from "./shell/useActiveModules";
import { HomePage } from "./pages/HomePage";
import { ProfilePage } from "./pages/ProfilePage";
import { InvitePage } from "./pages/InvitePage";
import { GateLogoutPage, GatePage } from "./pages/GatePage";
import { Forbidden, NotFound } from "./pages/Status";
import { PortalLayout } from "./portal/PortalLayout";

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!session) {
    const portal = location.pathname === "/portal" || location.pathname.startsWith("/portal/");
    return <Navigate to={portal ? "/portal/entrar" : "/entrar"} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <>{children}</>;
}

/** Área logada: portal para clientes, painel para a equipe. */
function Protected() {
  const session = useSession();
  const mods = useActiveModules();
  const location = useLocation();
  const rank = session.org?.rank ?? 0;
  const isClientOnly = rank < roleRank("partner");

  if (location.pathname === "/portal" || location.pathname.startsWith("/portal/")) {
    return (
      <Routes>
        <Route path="/portal/*" element={<PortalLayout mods={mods} />} />
      </Routes>
    );
  }
  if (isClientOnly) return <Navigate to={location.pathname === "/perfil" ? "/portal/seguranca" : "/portal"} replace />;

  const routes = routesFor(mods);
  return (
    <Layout>
      {/* As telas dos módulos carregam sob demanda (lazyPage): o esqueleto ocupa o lugar enquanto o arquivo chega. */}
      <ScreenBoundary>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/perfil" element={<ProfilePage />} />
            {routes.map((r) => (
              <Route key={r.path} path={r.path} element={rank >= roleRank(r.minRole) ? r.element : <Forbidden />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ScreenBoundary>
    </Layout>
  );
}

const publicRoutes = registeredModules.flatMap((m) => m.ui.publicRoutes ?? []);

export function App() {
  return (
    <Routes>
      <Route path="/entrar" element={<LoginPage />} />
      <Route path="/portal/entrar" element={<LoginPage area="portal" />} />
      <Route
        path="/primeiro-acesso"
        element={
          <RequireAuth>
            <FirstAccessPage />
          </RequireAuth>
        }
      />
      <Route path="/recuperar-senha" element={<RecoverPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/convite/:token" element={<InvitePage />} />
      {/* Portão dos painéis de infraestrutura: login (e 2FA pelo SessionGate) antes de liberar o cookie do worker. */}
      <Route
        path="/portao"
        element={
          <RequireAuth>
            <SessionGate>
              <GatePage />
            </SessionGate>
          </RequireAuth>
        }
      />
      <Route path="/portao/sair" element={<GateLogoutPage />} />
      {publicRoutes.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={
            <ScreenBoundary>
              <Suspense fallback={<PageSkeleton />}>{r.element}</Suspense>
            </ScreenBoundary>
          }
        />
      ))}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <SessionGate>
              <Protected />
            </SessionGate>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
